/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { theme } from "../theme";
import { Box, Flex } from "./layout";
import Ripple from "./ripple";
import { IoIosArrowDown } from "react-icons/io";
import { InputWrapper } from "./input.style";
import { useOnClickOutside } from "./outside.click";
import { IoClose, IoSearch } from "react-icons/io5";

export const SelectField = ({
  label,
  required,
  placeholder = "",
  suffix,
  prefix = "true",
  variant = "outlined",
  size = "md",
  labelBold,
  error = false,
  helperText = "",
  disabled = false,
  fullWidth = false,
  valueSinc = false,
  color = "default",
  type = "text",
  options = [],
  defaultValue ,
  onChange,
  clearable = false,
  searchable = false, // New prop to control search functionality
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  // Handle default value
  useEffect(() => {
    
      const nameValue = options.find(it => it?.id === defaultValue)?.name;
      if (nameValue) {
        setValue(nameValue);
      }
      else if (typeof nameValue ==='boolean') {
        setValue(nameValue);
      }else{
        setValue("");
      }
    
  }, [defaultValue]);

  // Filter options based on search term if searchable is true
  const filteredOptions = searchable 
    ? options.filter(option =>
        option.name.toLowerCase().includes((dropdownVisible ? searchTerm : value).toLowerCase())
      )
    : options;

  const handleSelect = (option) => {
    if (option.disabled) return;
    setValue(option.name);
    setSearchTerm('');
    setDropdownVisible(false);
    setHighlightedIndex(-1);
    if (onChange) onChange(option.id);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setValue("");
    setSearchTerm("");
    if (onChange) onChange(null);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!dropdownVisible) {
          setDropdownVisible(true);
        } else {
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setDropdownVisible(false);
        setHighlightedIndex(-1);
        if (!value) setSearchTerm('');
        break;
      default:
        if (!searchable) {
          // Prevent typing in the input when not searchable
          e.preventDefault();
        }
        break;
    }
  };

  useOnClickOutside(dropdownRef, () => {
    setDropdownVisible(false);
    setHighlightedIndex(-1);
    if (!value) setSearchTerm('');
  });

  const handleInputChange = (e) => {
    if (!searchable) return;
    
    const newValue = e.target.value;
    setSearchTerm(newValue);
    if (!dropdownVisible) {
      setDropdownVisible(true);
    }
  };

  
  return (
    <InputWrapper
      label={label}
      variant={variant}
      error={error}
      disabled={disabled}
      mainColor={theme.colors[color]?.main || theme.colors.default.dark}
      errorColor={theme.colors.danger.main}
      isFocused={focused}
      fullWidth={fullWidth}
      valueSinc={valueSinc}
      size={size}
      suffix={suffix}
      prefix={prefix}
      labelBold={labelBold}
      dropdownVisible={dropdownVisible}
    >
      {suffix && (
        <Flex className="suffix" alignItem="center" justifyContent="center">
          {suffix}
        </Flex>
      )}
      {prefix && (
        <Flex className="prefix" alignItem="center" justifyContent="center">
          {searchable && dropdownVisible ? <IoSearch /> : <IoIosArrowDown />}
        </Flex>
      )}
      {clearable && value && (
        <Flex
          className="close"
          alignItem="center"
          justifyContent="center"
          onClick={handleClear}
        >
          <IoClose />
        </Flex>
      )}
      <input
        ref={inputRef}
        type={type}
        {...props}
        readOnly={!searchable}
        value={searchable && dropdownVisible ? searchTerm : value}
        placeholder={
          variant === "standard"
            ? placeholder
            : label
              ? focused && !value && !valueSinc
                ? placeholder
                : ""
              : !value
                ? placeholder
                : ""
        }
        disabled={disabled}
        onFocus={() => {
          setFocused(true);
          setDropdownVisible(true);
        }}
        onBlur={() => setFocused(false)}
        onClick={() => !disabled && setDropdownVisible(true)}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={dropdownVisible}
        aria-activedescendant={highlightedIndex >= 0 ? `option-${filteredOptions[highlightedIndex]?.id}` : undefined}
        name={label}
        id={label}
        autoComplete="off"
      />
      {label && (
        <label  htmlFor={label}
          className={`${focused || value || valueSinc ? "focused" : ""} ${
            error ? "error" : ""
          } ${disabled ? "disabled" : ""}`}
        >
          {label} {required && <Box className="required">*</Box>}
        </label>
      )}
      <fieldset
        className={`${focused || value || valueSinc ? "focused" : ""} ${
          error ? "error" : ""
        } ${!label && "noLabel"}`}
      >
        {label && (
          <legend>
            <span>
              {label}
              {required && (
                <span
                  style={{
                    color: theme.colors.danger.main,
                    paddingInlineEnd: 0,
                  }}
                />
              )}
            </span>
          </legend>
        )}
      </fieldset>
      {dropdownVisible && (
        <ul id="select-dropdown" ref={dropdownRef} className="dropdown" role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
              key={option.id}
              id={`option-${option.id}`}
              className={`dropdownItem ${value === option.name ? "selected" : ""} ${
                index === highlightedIndex ? "highlighted" : ""
              } ${option.disabled && "disabled"}`} // Apply disabled class
              onClick={() => !option.disabled && handleSelect(option)} // Prevent selection if disabled
              role="option"
              aria-selected={value === option.name}
            >
                {option.name}
                {!option.disabled && <Ripple duration={1000} />}
              </li>
            ))
          ) : (
            <li className="no-options" role="option" aria-disabled="true">
              No options available
            </li>
          )}
        </ul>
      )}
      {helperText && <Box className="helperText">{helperText}</Box>}
    </InputWrapper>
  );
};

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf(["outlined", "filled", "standard"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  valueSinc: PropTypes.bool,
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "danger",
    "default",
  ]),
  type: PropTypes.string,
  required: PropTypes.bool,
  searchable: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

SelectField.defaultProps = {
  label: "",
  placeholder: "",
  variant: "outlined",
  size: "md",
  error: false,
  helperText: "",
  disabled: false,
  fullWidth: false,
  color: "primary",
  type: "text",
  valueSinc: false,
  required: false,
  searchable: false,
  options: [],
};